import { useMemo, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { Panel } from "../../../../components/panels/Panel";
import { useOrgData } from "../../../../contexts/OrgDataContext";
import {
  _getDatasetDisplayData,
  getLineDatasetsFromPoints,
  mergeDatasetsByChartId
} from "../../../../services/entity/chart/chart-service";
import { GenericChart } from "../../../dashboard/components/charts/GenericChart";
import { useJournal } from "../../../../contexts/JournalContext";
import { InboxIcon } from "@heroicons/react/20/solid";
import Badge from "../../../../components/badges/Badge";
import { Loader } from "../../../../components/loading/Loader";

Chart.register(...registerables);

export const DashboardPanel = () => {
  const { workstationSelected } = useOrgData();
  const { logs, lastLogRetrievalDate, isLogsProcessing, focusedLogDate } = useJournal();

  const graphDiv = useRef(null);

  const charts = [{ id: 'minidashboard', type: 'line' }];

  const _createLogsBins = (startDate, stopDate, pointsNumber, logs) => {
    const span = stopDate - startDate;
    if (!Number.isFinite(span) || span <= 0 || pointsNumber <= 0) return [];
    const step = span / pointsNumber;

    const counts = new Array(pointsNumber).fill(0);
    for (const { time } of logs) {
      const t = Date.parse(time);
      if (!Number.isFinite(t) || t < startDate || t > stopDate) continue;
      const i = Math.min(pointsNumber - 1, Math.floor((t - startDate) / step)); // inclut stopDate dans le dernier bucket
      counts[i]++;
    }

    return counts.map((c, i) => ({ time: Math.round(startDate + i * step), value: c }));
  };

  const selectedDateRange = {
    id: 1, // Crash if not present
    start: lastLogRetrievalDate.current,
    stop: Date.now(),
  };

  // Compute graphs data depending on loaded logs
  const graphsData = useMemo(() => {
    if (logs?.length === 0) return null;

    // Simulate a fake chart/chartData pair to use generic graphs functions
    const activityChart = {
      id: 'activity_chart',
      type: 'line',
    };

    const activitiesData = [];

    // To separate graphs based on subtype, uncomment the loop
    // for (const [subtype, points] of Object.entries(splitPointsBySubtype(logs))) {
      const activityChartData = {
        id: 'activity_chart_data',
        series_name: 'Activity',
        series_color: 'rgb(65, 100, 253)',
      };

      // const logsBins = _createLogsBins(selectedDateRange.start, selectedDateRange.stop, 50, points);
      const logsBins = _createLogsBins(selectedDateRange.start, selectedDateRange.stop, 50, logs);
      const activityData = _getDatasetDisplayData(activityChart, activityChartData, logsBins, selectedDateRange);
      activitiesData.push(activityData);
    // }

    // We format our datasets to be suitable for what the Chart.js library expects for building a graph.
    const allDatasets = activitiesData.map((dataset, datasetIndex) => {
      return getLineDatasetsFromPoints(dataset, datasetIndex, selectedDateRange);
    });

    // We merge all datasets sharing the same chart ID to have only one dataset for a graph.
    const mergeDatasets = mergeDatasetsByChartId(allDatasets);

    return Object.entries(mergeDatasets).map(([chartId, datasets]) => ({
      chartId: chartId,
      datasets,
    }))[0];
  }, [logs]);


  return (
    <Panel title="ACTIVITY">
      {
        isLogsProcessing ?
          <div className='flex relative items-center justify-center h-full'>
            <Loader category="transparent" />
          </div>

        : graphsData === null || Object.entries(graphsData).length === 0 ?
          <div className="flex flex-col items-center justify-center h-full text-perception-gray-700 gap-2 text-center">
            <InboxIcon className="size-16" />
            <span className="text-xl">No logs found</span>
          </div>

        :
          <div className="flex flex-col items-start h-full" ref={graphDiv}>
            <Badge text={`${workstationSelected.name} activity rate (logs number)`} type="perception"/>
            <GenericChart
              charts={charts}
              type='main-graph'
              graphsData={graphsData}
              isLoading={false}
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={null}
              minify={true}
              focusDate={focusedLogDate}
              className="w-full"
            />
          </div>
      }
    </Panel>
  );
};
